import { useEffect, useState, useCallback } from "react";
import { Search, Eye } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { api } from "../lib/api";

export default function AdminPatients() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [viewData, setViewData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "20" });
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/patients?${params.toString()}`);
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!viewId) return;
    api.get(`/admin/patients/${viewId}`).then((r) => setViewData(r.data));
  }, [viewId]);

  return (
    <AdminLayout title="Patients" subtitle={`${total} total`}>
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-patients-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search phone/name/email" className="pl-9 rounded-full" />
        </div>
      </div>

      <div data-testid="admin-patients-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left p-3">Phone</th><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th>
                <th className="text-right p-3">Appts</th><th className="text-left p-3">Joined</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No patients</td></tr>
                : items.map((p) => (
                <tr key={p.id} data-testid={`admin-patient-row-${p.id}`} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3 font-mono text-xs">{p.phone}</td>
                  <td className="p-3">{p.name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 text-xs">{p.email || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 text-right tabular-nums">{p.appointment_count}</td>
                  <td className="p-3 text-xs text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" data-testid={`admin-patient-view-${p.id}`} onClick={() => setViewId(p.id)}><Eye className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 20 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <Sheet open={!!viewId} onOpenChange={(o) => { if (!o) { setViewId(null); setViewData(null); } }}>
        <SheetContent data-testid="admin-patient-view-drawer" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>{viewData?.name || viewData?.phone || "Patient"}</SheetTitle></SheetHeader>
          {viewData && (
            <div className="space-y-4 mt-4">
              <div className="bg-secondary/40 rounded-xl p-3 text-sm">
                <div className="font-mono text-xs">{viewData.phone}</div>
                <div className="text-xs">{viewData.email || "—"}</div>
                {viewData.dob && <div className="text-xs">DOB: {viewData.dob}</div>}
                {viewData.gender && <div className="text-xs">Gender: {viewData.gender}</div>}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-white border border-border rounded-xl p-2"><div className="text-muted-foreground">Appts</div><div className="font-semibold text-base">{viewData.appointment_count}</div></div>
                <div className="bg-white border border-border rounded-xl p-2"><div className="text-muted-foreground">Rx</div><div className="font-semibold text-base">{viewData.prescription_count}</div></div>
                <div className="bg-white border border-border rounded-xl p-2"><div className="text-muted-foreground">Vitals</div><div className="font-semibold text-base">{viewData.vitals_count}</div></div>
              </div>
              {viewData.last_appointment && (
                <div className="bg-white border border-border rounded-xl p-3 text-sm">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Last appointment</div>
                  <div className="font-medium">{viewData.last_appointment.doctor?.name}</div>
                  <div className="text-xs text-muted-foreground">{viewData.last_appointment.mode} · {viewData.last_appointment.status} · {viewData.last_appointment.scheduled_at_local}</div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
